import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { apiPostJson } from "@/lib/api";
import { STORAGE_LEADS } from "@/constants/storage";

const LEAD_KEY = STORAGE_LEADS.leadSubmitted;

// "chat" is the original chat-bubble styling; "explore" restyles to the
// serif / CSS-token look of the Explore project-detail aside (PROP-248) so
// the referral CTA doesn't drop chat chrome into that surface.
type Variant = "chat" | "explore";

// LeadCta invites the (public, unauthenticated) visitor to be referred to a
// licensed agent via POST /leads/capture — the lead lands unassigned in the
// pro-agent claim pool (PROP-186). This is referral to an external licensed
// agent, deliberately distinct from ProProp acting as an estate agency.
export function LeadCta({
  source,
  context,
  headline,
  sub,
  variant = "chat",
}: {
  source: string;
  // Server-derived identity (e.g. project name + slug) that is ALWAYS attached
  // to the lead so the claiming agent knows what it's about — never shown in
  // the editable note field, never reconstructed from client state.
  context?: string;
  headline?: string;
  sub?: string;
  variant?: Variant;
}) {
  const { t } = useTranslation();
  // Initialise once on mount — never re-check so success state isn't wiped
  // by the localStorage flag being set during the same render cycle.
  const [alreadySubmitted] = useState(
    () => typeof window !== "undefined" && !!localStorage.getItem(LEAD_KEY),
  );
  const [open, setOpen] = useState(false);

  if (alreadySubmitted) return null;

  const h = headline ?? (t("leadCta.headline") as string);
  const s = sub ?? (t("leadCta.sub") as string);

  if (!open) {
    return variant === "explore" ? (
      <ExploreTrigger headline={h} sub={s} onOpen={() => setOpen(true)} />
    ) : (
      <ChatTrigger headline={h} sub={s} onOpen={() => setOpen(true)} />
    );
  }

  return (
    <LeadForm source={source} context={context} variant={variant} onDismiss={() => setOpen(false)} />
  );
}

function ChatTrigger({ headline, sub, onOpen }: { headline: string; sub: string; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group mt-3 flex w-full items-center gap-3 rounded-xl border border-accent/25 bg-gradient-to-r from-accent/10 to-purple-500/10 px-4 py-3 text-left transition hover:border-accent/50 hover:from-accent/15"
    >
      <span className="text-xl">🤝</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-text">{headline}</span>
        <span className="block text-[12px] text-text-sec">{sub}</span>
      </span>
      <span className="text-lg text-text-muted transition group-hover:translate-x-0.5 group-hover:text-text">›</span>
    </button>
  );
}

function ExploreTrigger({ headline, sub, onOpen }: { headline: string; sub: string; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 11,
        width: "100%",
        textAlign: "left",
        border: "1px solid var(--accent)",
        background: "var(--accent-soft, var(--surface))",
        borderRadius: 10,
        padding: "12px 14px",
        cursor: "pointer",
        marginBottom: 16,
        fontFamily: "var(--font-ui)",
      }}
    >
      <span style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            display: "block",
            fontFamily: "var(--font-serif)",
            fontSize: 14,
            fontWeight: 500,
            color: "var(--ink)",
            letterSpacing: "-0.01em",
          }}
        >
          {headline}
        </span>
        <span style={{ display: "block", marginTop: 2, fontSize: 12, color: "var(--ink-3)" }}>{sub}</span>
      </span>
      <span style={{ fontSize: 18, color: "var(--accent)", flex: "none" }}>›</span>
    </button>
  );
}

function LeadForm({
  source,
  context,
  variant,
  onDismiss,
}: {
  source: string;
  context?: string;
  variant: Variant;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!name.trim() || !phone.trim()) return;
    setSending(true);
    setErr(null);
    try {
      // Attach the server-derived context (project identity) first, then the
      // visitor's optional note; the server re-clamps to its own max.
      const payloadContext = [context, note.trim()].filter(Boolean).join(" — ").slice(0, 300);
      await apiPostJson("/leads/capture", {
        name: name.trim(),
        phone: phone.trim(),
        source,
        context: payloadContext,
      });
      localStorage.setItem(LEAD_KEY, "1");
      setSuccess(true);
    } catch (e) {
      setErr((e as Error).message || (t("leadCta.sendFailed") as string));
    } finally {
      setSending(false);
    }
  }

  const explore = variant === "explore";
  const containerStyle: CSSProperties = explore
    ? {
        position: "relative",
        marginBottom: 16,
        borderRadius: 10,
        border: "1px solid var(--rule)",
        background: "var(--surface)",
        padding: "14px 16px",
      }
    : {};
  const dismissStyle: CSSProperties = explore
    ? {
        position: "absolute",
        right: 8,
        top: 8,
        width: 24,
        height: 24,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 6,
        border: "1px solid var(--rule)",
        background: "transparent",
        color: "var(--ink-3)",
        cursor: "pointer",
      }
    : {};

  return (
    <div
      className={explore ? undefined : "msg-in relative mt-3 rounded-xl border border-accent/25 p-4 pr-10"}
      style={
        explore
          ? containerStyle
          : {
              background: "linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(139,92,246,0.06) 100%)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
            }
      }
    >
      <button
        aria-label={t("leadCta.dismiss", { defaultValue: "Dismiss" }) as string}
        onClick={onDismiss}
        className={explore ? undefined : "absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-md border border-border text-text-muted hover:text-text"}
        style={explore ? dismissStyle : undefined}
      >
        ×
      </button>
      {success ? (
        <div
          className={explore ? undefined : "flex items-center gap-2 text-[13px] font-medium text-good"}
          style={explore ? { display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 500, color: "var(--pos, var(--accent))" } : undefined}
        >
          <span>✓</span> {t("leadCta.success")}
        </div>
      ) : (
        <div className={explore ? undefined : "space-y-2"} style={explore ? { display: "flex", flexDirection: "column", gap: 8 } : undefined}>
          <div
            className={explore ? undefined : "text-[14px] font-bold text-text"}
            style={explore ? { fontFamily: "var(--font-serif)", fontSize: 15, fontWeight: 500, color: "var(--ink)" } : undefined}
          >
            {t("leadCta.formTitle")}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              className="input-field flex-1"
              placeholder={t("leadCta.name", { defaultValue: "Your name" }) as string}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              className="input-field flex-1"
              placeholder={t("leadCta.phone", { defaultValue: "WhatsApp / phone" }) as string}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <textarea
            className="input-field min-h-[60px]"
            placeholder={t("leadCta.lookingFor", { defaultValue: "What are you looking for? (optional)" }) as string}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {err && (
            <div className={explore ? undefined : "text-[12px] text-bad"} style={explore ? { fontSize: 12, color: "var(--neg, #bf3838)" } : undefined}>
              {err}
            </div>
          )}
          <button
            className="btn-primary w-full sm:w-auto"
            onClick={submit}
            disabled={sending || !name.trim() || !phone.trim()}
          >
            {sending ? t("leadCta.sending") : t("leadCta.submit")}
          </button>
        </div>
      )}
    </div>
  );
}
